import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKeyhole, Loader2 } from "lucide-react";
import api, { setToken, formatApiError } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import { Field, inputProps } from "@/components/Field";

export const PortalShell = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-blush-50 px-5 py-16">
    <div className="w-full max-w-md">
      <div className="mb-8 text-center">
        <Link to="/">
          <img src="/logo.png" alt="New Beginnings Healthcare Services, LLC" className="mx-auto h-20 w-auto" />
        </Link>
      </div>
      <div className="rounded-[2rem] border border-blush-200 bg-white p-8 shadow-[0_10px_44px_rgba(31,41,55,0.08)] sm:p-10">
        <h1 className="text-center font-serif text-2xl text-charcoal">{title}</h1>
        <p className="mt-2 text-center text-sm text-charcoal-muted">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const AdminLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.user.role !== "admin") {
        toast.error("This area is for administrators. Employees should use the onboarding portal.");
        return;
      }
      setToken(data.token);
      navigate("/admin/dashboard", { state: { user: data.user } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalShell title="Administrator Login" subtitle="Authorized administrators only. All activity is logged.">
      <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="admin-login-form">
        <Field label="Email" htmlFor="al-email" required>
          <input id="al-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" {...inputProps("admin-login-email-input")} />
        </Field>
        <Field label="Password" htmlFor="al-password" required>
          <input id="al-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" {...inputProps("admin-login-password-input")} />
        </Field>
        <button type="submit" data-testid="admin-login-submit" disabled={loading} className="btn-pink w-full disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LockKeyhole className="h-4 w-4" />}
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-blush-200" />
        <span className="text-xs uppercase tracking-[0.18em] text-charcoal-faint">or</span>
        <span className="h-px flex-1 bg-blush-200" />
      </div>
      <GoogleButton label="Sign in with Google" />
      <p className="mt-6 text-center text-xs leading-relaxed text-charcoal-faint">
        Employee onboarding?{" "}
        <Link to="/onboarding" className="font-semibold text-gold-deep hover:underline">
          Go to the onboarding portal
        </Link>
      </p>
    </PortalShell>
  );
};

export default AdminLogin;
